import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectFilterState,
  selectActiveFilters,
  setView as setSegView,
  setFeatures as setSegFeatures,
  setIncludeAllSegmentationLabels as setSegIncludeAllSegmentationLabels,
  setAdvancedFilter as setSegAdvancedFilter,
  resetFilters as resetSegFilters,
  updateActiveTags as updateSegActiveTags,
  removeFilterTag as removeSegFilterTag,
} from '../redux/slices/filterState';
import {
  selectKeypointFilterState,
  selectKeypointActiveFilters,
  setView as setKpView,
  setFeatures as setKpFeatures,
  setAdvancedFilter as setKpAdvancedFilter,
  resetFilters as resetKpFilters,
  updateActiveTags as updateKpActiveTags,
  removeFilterTag as removeKpFilterTag,
} from '../redux/slices/keypointFilterState';
import {
  selectViewLabelFilterState,
  selectViewLabelActiveFilters,
  setView as setVlView,
  setFeatures as setVlFeatures,
  setAdvancedFilter as setVlAdvancedFilter,
  resetFilters as resetVlFilters,
  updateActiveTags as updateVlActiveTags,
  removeFilterTag as removeVlFilterTag,
  setShouldFetchViewLabels as setVlShouldFetchViewLabels,
  setPage as setVlPage,
  setSize as setVlSize,
} from '../redux/slices/viewLabelFilterState';
import {
  selectSegmentationPredictionFilterState,
  selectSegmentationPredictionActiveFilters,
  setView as setSegPredView,
  setFeatures as setSegPredFeatures,
  setAdvancedFilter as setSegPredAdvancedFilter,
  resetFilters as resetSegPredFilters,
  updateActiveTags as updateSegPredActiveTags,
  removeFilterTag as removeSegPredFilterTag,
} from '../redux/slices/segmentationPredictionFilterState';
import {
  selectPredictionFilterState,
  selectPredictionActiveFilters,
  setPredictionFilter as setPredFilter,
  resetFilters as resetPredFilters,
  updateActiveTags as updatePredActiveTags,
} from '../redux/slices/predictionFilterState';
import { AdvancedFilterValue } from '../models/advancedFilters';

export const useFilterState = () => {
  const dispatch = useDispatch();
  const filterState = useSelector(selectFilterState);
  const activeTags = useSelector(selectActiveFilters);

  const handleSetView = useCallback(
    (view: AdvancedFilterValue | undefined) => {
      dispatch(setSegView(view));
    },
    [dispatch]
  );

  const handleSetFeatures = useCallback(
    (features: AdvancedFilterValue[]) => {
      dispatch(setSegFeatures(features));
    },
    [dispatch]
  );

  const handleSetIncludeAllSegmentationLabels = useCallback(
    (includeAllSegmentationLabels: boolean) => {
      dispatch(setSegIncludeAllSegmentationLabels(includeAllSegmentationLabels));
    },
    [dispatch]
  );

  const handleSetAdvancedFilter = useCallback(
    (
      key: string,
      value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined
    ) => {
      dispatch(setSegAdvancedFilter({ key, value }));
    },
    [dispatch]
  );

  const handleResetFilters = useCallback(() => {
    dispatch(resetSegFilters());
    dispatch(updateSegActiveTags([]));
  }, [dispatch]);

  const handleRemoveTag = useCallback(
    (tag: AdvancedFilterValue, onlyActiveTags = true) => {
      if (onlyActiveTags) {
        dispatch(
          updateSegActiveTags(activeTags.filter((t: AdvancedFilterValue) => t.uuid !== tag.uuid))
        );
      } else {
        dispatch(removeSegFilterTag(tag));
      }
    },
    [dispatch, activeTags]
  );

  const updateFilterTags = useCallback(() => {
    const currentState = filterState;
    const tags: AdvancedFilterValue[] = [];

    if (currentState.view) {
      tags.push({
        uuid: currentState.view.uuid,
        name: `view: ${currentState.view.name}`,
      });
    }

    currentState.features.forEach(feature => {
      tags.push({
        uuid: feature.uuid,
        name: `feature: ${feature.name}`,
      });
    });

    Object.entries(currentState.advancedFilters).forEach(([key, value]: [string, any]) => {
      const tagKey = key;
      if (value) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            if (item && item.uuid && item.name) {
              tags.push({
                uuid: item.uuid,
                name: `${tagKey}: ${item.name}`,
              });
            } else if (typeof item === 'string' && item.trim()) {
              tags.push({
                uuid: `${key}_${item}`,
                name: `${tagKey}: ${item}`,
              });
            }
          });
        } else if (typeof value === 'string') {
          if (value.trim()) {
            tags.push({
              uuid: value,
              name: `${tagKey}: ${value}`,
            });
          }
        } else if ((value as any).uuid && (value as any).name) {
          tags.push({
            uuid: (value as any).uuid,
            name: `${tagKey}: ${(value as any).name}`,
          });
        }
      }
    });

    dispatch(updateSegActiveTags(tags));
  }, [dispatch, filterState]);
  return {
    filterState,
    activeTags,
    setView: handleSetView,
    setFeatures: handleSetFeatures,
    setIncludeAllSegmentationLabels: handleSetIncludeAllSegmentationLabels,
    setAdvancedFilter: handleSetAdvancedFilter,
    resetFilters: handleResetFilters,
    removeTag: handleRemoveTag,
    updateFilterTags,
  };
};

export const useKeypointFilterState = () => {
  const dispatch = useDispatch();
  const filterState = useSelector(selectKeypointFilterState);
  const activeTags = useSelector(selectKeypointActiveFilters);

  const handleSetView = useCallback(
    (view: AdvancedFilterValue | undefined) => {
      dispatch(setKpView(view));
    },
    [dispatch]
  );

  const handleSetFeatures = useCallback(
    (features: AdvancedFilterValue[]) => {
      dispatch(setKpFeatures(features));
    },
    [dispatch]
  );

  const handleSetAdvancedFilter = useCallback(
    (
      key: string,
      value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined
    ) => {
      dispatch(setKpAdvancedFilter({ key, value }));
    },
    [dispatch]
  );

  const handleResetFilters = useCallback(() => {
    dispatch(resetKpFilters());
    dispatch(updateKpActiveTags([]));
  }, [dispatch]);

  const handleRemoveTag = useCallback(
    (tag: AdvancedFilterValue, onlyActiveTags = true) => {
      if (onlyActiveTags) {
        dispatch(
          updateKpActiveTags(activeTags.filter((t: AdvancedFilterValue) => t.uuid !== tag.uuid))
        );
      } else {
        dispatch(removeKpFilterTag(tag));
      }
    },
    [dispatch, activeTags]
  );

  const updateFilterTags = useCallback(() => {
    const currentState: any = filterState;
    const tags: AdvancedFilterValue[] = [];

    if (currentState.view) {
      tags.push({
        uuid: currentState.view.uuid,
        name: `view: ${currentState.view.name}`,
      });
    }

    currentState.features.forEach((feature: any) => {
      tags.push({
        uuid: feature.uuid,
        name: `feature: ${feature.name}`,
      });
    });

    Object.entries(currentState.advancedFilters).forEach(([key, value]: any) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((item: any) => {
            if (item && item.uuid && item.name) {
              tags.push({
                uuid: item.uuid,
                name: `${key}: ${item.name}`,
              });
            } else if (typeof item === 'string' && item.trim()) {
              tags.push({
                uuid: `${key}_${item}`,
                name: `${key}: ${item}`,
              });
            }
          });
        } else if (typeof value === 'string') {
          if (value.trim()) {
            tags.push({
              uuid: value,
              name: `${key}: ${value}`,
            });
          }
        } else if ((value as any).uuid && (value as any).name) {
          tags.push({
            uuid: (value as any).uuid,
            name: `${key}: ${(value as any).name}`,
          });
        }
      }
    });

    dispatch(updateKpActiveTags(tags));
  }, [dispatch, filterState]);

  return {
    filterState,
    activeTags,
    setView: handleSetView,
    setFeatures: handleSetFeatures,
    setAdvancedFilter: handleSetAdvancedFilter,
    resetFilters: handleResetFilters,
    removeTag: handleRemoveTag,
    updateFilterTags,
  };
};

export const useViewLabelFilterState = () => {
  const dispatch = useDispatch();
  const filterState = useSelector(selectViewLabelFilterState);
  const activeTags = useSelector(selectViewLabelActiveFilters);

  const handleSetView = useCallback(
    (view: AdvancedFilterValue | undefined) => {
      dispatch(setVlView(view));
    },
    [dispatch]
  );

  const handleSetFeatures = useCallback(
    (features: AdvancedFilterValue[]) => {
      dispatch(setVlFeatures(features));
    },
    [dispatch]
  );

  const handleSetAdvancedFilter = useCallback(
    (
      key: string,
      value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined
    ) => {
      dispatch(setVlAdvancedFilter({ key, value }));
    },
    [dispatch]
  );

  const handleResetFilters = useCallback(() => {
    dispatch(resetVlFilters());
    dispatch(updateVlActiveTags([]));
  }, [dispatch]);

  const handleRemoveTag = useCallback(
    (tag: AdvancedFilterValue, onlyActiveTags = true) => {
      if (onlyActiveTags) {
        dispatch(
          updateVlActiveTags(activeTags.filter((t: AdvancedFilterValue) => t.uuid !== tag.uuid))
        );
      } else {
        dispatch(removeVlFilterTag(tag));
      }
    },
    [dispatch, activeTags]
  );

  const updateFilterTags = useCallback(() => {
    const currentState: any = filterState;
    const newTags: AdvancedFilterValue[] = [];

    if (currentState.view) {
      newTags.push({
        uuid: currentState.view.uuid,
        name: `view: ${currentState.view.name}`,
      });
    }

    currentState.features.forEach((feature: any) => {
      newTags.push({
        uuid: feature.uuid,
        name: `feature: ${feature.name}`,
      });
    });

    Object.entries(currentState.advancedFilters).forEach(([key, value]: any) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((item: any) => {
            if (item && item.uuid && item.name) {
              newTags.push({
                uuid: item.uuid,
                name: `${key}: ${item.name}`,
              });
            } else if (typeof item === 'string' && item.trim()) {
              newTags.push({
                uuid: `${key}_${item}`,
                name: `${key}: ${item}`,
              });
            }
          });
        } else if (typeof value === 'string') {
          if (value.trim()) {
            newTags.push({
              uuid: value,
              name: `${key}: ${value}`,
            });
          }
        } else if ((value as any).uuid && (value as any).name) {
          newTags.push({
            uuid: (value as any).uuid,
            name: `${key}: ${(value as any).name}`,
          });
        }
      }
    });

    // Check if newTags are different from activeTags before dispatching
    const areTagsEqual = (arr1: AdvancedFilterValue[], arr2: AdvancedFilterValue[]) => {
      if (arr1.length !== arr2.length) return false;
      const sortedArr1 = [...arr1].sort((a, b) => a.uuid.localeCompare(b.uuid));
      const sortedArr2 = [...arr2].sort((a, b) => a.uuid.localeCompare(b.uuid));
      return sortedArr1.every(
        (tag, index) => tag.uuid === sortedArr2[index].uuid && tag.name === sortedArr2[index].name
      );
    };

    if (!areTagsEqual(newTags, activeTags)) {
      dispatch(updateVlActiveTags(newTags));
    }
  }, [dispatch, filterState, activeTags]);

  const handleSetShouldFetchViewLabels = useCallback(
    (shouldFetch: boolean) => {
      dispatch(setVlShouldFetchViewLabels(shouldFetch));
    },
    [dispatch]
  );

  return {
    filterState,
    activeTags,
    setView: handleSetView,
    setFeatures: handleSetFeatures,
    setAdvancedFilter: handleSetAdvancedFilter,
    resetFilters: handleResetFilters,
    removeTag: handleRemoveTag,
    updateFilterTags,
    setShouldFetchViewLabels: handleSetShouldFetchViewLabels,
    setPage: (page: number) => dispatch(setVlPage(page)),
    setSize: (size: number) => dispatch(setVlSize(size)),
  };
};

export const useSegmentationPredictionFilterState = (refetch?: () => void) => {
  const dispatch = useDispatch();
  const filterState = useSelector(selectSegmentationPredictionFilterState);
  const activeTags = useSelector(selectSegmentationPredictionActiveFilters);

  const handleSetView = useCallback(
    (view: AdvancedFilterValue | undefined) => {
      dispatch(setSegPredView(view));
    },
    [dispatch]
  );

  const handleSetFeatures = useCallback(
    (features: AdvancedFilterValue[]) => {
      dispatch(setSegPredFeatures(features));
    },
    [dispatch]
  );

  const handleSetAdvancedFilter = useCallback(
    (
      key: string,
      value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined
    ) => {
      dispatch(setSegPredAdvancedFilter({ key, value }));
    },
    [dispatch]
  );

  const handleResetFilters = useCallback(() => {
    dispatch(resetSegPredFilters());
    dispatch(updateSegPredActiveTags([]));
    if (refetch) {
      refetch();
    }
  }, [dispatch, refetch]);

  const handleRemoveTag = useCallback(
    (tag: AdvancedFilterValue, onlyActiveTags = true) => {
      if (onlyActiveTags) {
        dispatch(
          updateSegPredActiveTags(
            activeTags.filter((t: AdvancedFilterValue) => t.uuid !== tag.uuid)
          )
        );
      } else {
        dispatch(removeSegPredFilterTag(tag));
      }
    },
    [dispatch, activeTags]
  );

  const updateFilterTags = useCallback(() => {
    const currentState: any = filterState;
    const tags: AdvancedFilterValue[] = [];

    if (currentState.view) {
      tags.push({
        uuid: currentState.view.uuid,
        name: `view: ${currentState.view.name}`,
      });
    }

    currentState.features.forEach((feature: any) => {
      tags.push({
        uuid: feature.uuid,
        name: `feature: ${feature.name}`,
      });
    });

    Object.entries(currentState.advancedFilters).forEach(([key, value]: any) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((item: any) => {
            if (item && item.uuid && item.name) {
              tags.push({
                uuid: item.uuid,
                name: `${key}: ${item.name}`,
              });
            } else if (typeof item === 'string' && item.trim()) {
              tags.push({
                uuid: `${key}_${item}`,
                name: `${key}: ${item}`,
              });
            }
          });
        } else if (typeof value === 'string') {
          if (value.trim()) {
            tags.push({
              uuid: value,
              name: `${key}: ${value}`,
            });
          }
        } else if ((value as any).uuid && (value as any).name) {
          tags.push({
            uuid: (value as any).uuid,
            name: `${key}: ${(value as any).name}`,
          });
        }
      }
    });

    dispatch(updateSegPredActiveTags(tags));
  }, [dispatch, filterState]);

  return {
    filterState,
    activeTags,
    setView: handleSetView,
    setFeatures: handleSetFeatures,
    setAdvancedFilter: handleSetAdvancedFilter,
    resetFilters: handleResetFilters,
    removeTag: handleRemoveTag,
    updateFilterTags,
  };
};

export const usePredictionFilterState = () => {
  const dispatch = useDispatch();
  const filterState = useSelector(selectPredictionFilterState);
  const activeTags = useSelector(selectPredictionActiveFilters);

  const handleSetPredictionFilter = useCallback(
    (key: string, value: any) => {
      dispatch(setPredFilter({ key: key as any, value }));
    },
    [dispatch]
  );

  const handleResetFilters = useCallback(() => {
    dispatch(resetPredFilters());
    dispatch(updatePredActiveTags([]));
  }, [dispatch]);

  const updateFilterTags = useCallback(() => {
    const currentState: any = filterState;
    const tags: AdvancedFilterValue[] = [];

    // Add prediction-specific filters to tags
    if (currentState.is_reviewed !== undefined) {
      tags.push({
        uuid: `is_reviewed_${currentState.is_reviewed}`,
        name: `Review Status: ${currentState.is_reviewed ? 'Reviewed' : 'Unreviewed'}`,
      });
    }

    if (currentState.review_decision) {
      tags.push({
        uuid: `review_decision_${currentState.review_decision}`,
        name: `Decision: ${currentState.review_decision}`,
      });
    }

    if (currentState.has_corresponding_label !== undefined) {
      tags.push({
        uuid: `has_label_${currentState.has_corresponding_label}`,
        name: `AI Comparison: ${currentState.has_corresponding_label ? 'Ready' : 'No Label'}`,
      });
    }

    if (currentState.features && currentState.features.length > 0) {
      currentState.features.forEach((feature: AdvancedFilterValue) => {
        tags.push({
          uuid: `feature_${feature.uuid}`,
          name: `Feature: ${feature.name}`,
        });
      });
    }

    if (currentState.datasource) {
      tags.push({
        uuid: `datasource_${currentState.datasource.uuid}`,
        name: `Datasource: ${currentState.datasource.name}`,
      });
    }

    if (currentState.dicom_icid) {
      tags.push({
        uuid: `dicom_icid_${currentState.dicom_icid}`,
        name: `DICOM ICID: ${currentState.dicom_icid}`,
      });
    }

    // if (currentState.dicom_fid) {
    //   tags.push({
    //     uuid: `dicom_fid_${currentState.dicom_fid}`,
    //     name: `DICOM FID: ${currentState.dicom_fid}`,
    //   });
    // }

    if (currentState.study_icid) {
      tags.push({
        uuid: `study_icid_${currentState.study_icid}`,
        name: `Study ICID: ${currentState.study_icid}`,
      });
    }

    if (currentState.study_fid) {
      tags.push({
        uuid: `study_fid_${currentState.study_fid}`,
        name: `Study FID: ${currentState.study_fid}`,
      });
    }

    if (currentState.source_uuid) {
      tags.push({
        uuid: `source_uuid_${currentState.source_uuid}`,
        name: `Source UUID: ${currentState.source_uuid}`,
      });
    }

    if (currentState.created_at_after) {
      tags.push({
        uuid: `created_at_after_${currentState.created_at_after}`,
        name: `Created After: ${currentState.created_at_after}`,
      });
    }

    if (currentState.created_at_before) {
      tags.push({
        uuid: `created_at_before_${currentState.created_at_before}`,
        name: `Created Before: ${currentState.created_at_before}`,
      });
    }

    if (currentState.label_created_at_after) {
      tags.push({
        uuid: `label_created_at_after_${currentState.label_created_at_after}`,
        name: `Label Created After: ${currentState.label_created_at_after}`,
      });
    }

    if (currentState.label_created_at_before) {
      tags.push({
        uuid: `label_created_at_before_${currentState.label_created_at_before}`,
        name: `Label Created Before: ${currentState.label_created_at_before}`,
      });
    }

    dispatch(updatePredActiveTags(tags));
  }, [dispatch, filterState]);

  return {
    filterState,
    activeTags,
    setPredictionFilter: handleSetPredictionFilter,
    resetFilters: handleResetFilters,
    updateFilterTags,
  };
};
